import React from 'react';
import { Form, Formik, useFormikContext } from 'formik';
import { snakeCase } from 'lodash';
import * as Yup from 'yup';

import CardDialog from '~/components/CardDialog';
import CheckboxFormik from '~/components/CheckboxFormik';
import Button from '~/components/core/Atomic/Buttons/Button';
import ColoredDot from '~/components/core/Atomic/ColoredDot';
import MenuItem from '~/components/core/Atomic/MenuItem';
import TextFieldFormik, { useSetDefaultFieldsOnChange } from '~/components/TextFieldFormik';
import type {
  ClaimStageConfigurationCreateJson,
  ClaimStageConfigurationModel,
} from '~/server_shared/openapi/data-contracts';

import TEXTS from '../texts';

type LabelColor = ClaimStageConfigurationModel['color'];

const LABEL_COLORS = [
  'berry-600',
  'berry-700',
  'blue-600',
  'blueSky-700',
  'grey-600',
  'orange-700',
  'pink-600',
  'purple-600',
  'purple-700',
  'teal-600',
  'yellow-800',
] as const satisfies LabelColor[];

type ClaimStageAddEditDialogProps = {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (values: ClaimStageConfigurationCreateJson) => void;
  editedStage: ClaimStageConfigurationModel | null;
  existingKeys: string[];
};

const ClaimStageAddEditDialogInner = ({ isEdit, onCancel }: { isEdit: boolean; onCancel: () => void }) => {
  const { values, isSubmitting } = useFormikContext<ClaimStageConfigurationCreateJson>();
  useSetDefaultFieldsOnChange(values.label, { key: snakeCase(values.label) }, null, isEdit);

  return (
    <Form className="grid grid-cols-[auto_1fr_1fr] gap-x-8 gap-y-12">
      <TextFieldFormik select id="color" name="color" label={TEXTS.labels.color}>
        {/* todo: remove this and next 2 lines once TextFieldFormik converts to typescript */}
        {/* eslint-disable-next-line @typescript-eslint/ban-ts-comment */}
        {/* @ts-ignore */}
        {LABEL_COLORS.map((color) => (
          <MenuItem key={color} value={color}>
            <ColoredDot color={color} />
          </MenuItem>
        ))}
      </TextFieldFormik>
      <TextFieldFormik id="label" name="label" label={TEXTS.labels.label} />
      <TextFieldFormik id="key" name="key" label={TEXTS.labels.key} disabled={isEdit} />
      <div className="col-span-3">
        <CheckboxFormik id="is_initial" label={TEXTS.labels.initial} />
      </div>
      <div className="col-span-3 justify-self-end">
        <Button variant="outlined" color="primary" className="mr-16" onClick={onCancel} disabled={isSubmitting}>
          {TEXTS.cancel}
        </Button>
        <Button type="submit" variant="contained" color="primary" disabled={isSubmitting}>
          {TEXTS.save}
        </Button>
      </div>
    </Form>
  );
};

const ClaimStageAddEditDialog = ({
  isOpen,
  onClose,
  onSubmit,
  editedStage,
  existingKeys,
}: ClaimStageAddEditDialogProps) => {
  return (
    <CardDialog
      isDialog
      fullWidth
      open={isOpen}
      onClose={onClose}
      title={editedStage ? TEXTS.editStage : TEXTS.createStage}
    >
      <Formik
        initialValues={{
          label: editedStage?.label || '',
          color: editedStage?.color || 'blue-600',
          key: editedStage?.key || '',
          is_initial: editedStage?.is_initial ?? false,
        }}
        validationSchema={Yup.object().shape({
          label: Yup.string().trim().required(TEXTS.required),
          ...(editedStage
            ? {}
            : {
                key: Yup.string()
                  .trim()
                  .notOneOf(existingKeys, TEXTS.keyMustBeUnique)
                  .required(TEXTS.required)
                  .matches(/^[a-z0-9_]+$/, TEXTS.keyMustMatchPattern)
                  .matches(/[a-z0-9]/, TEXTS.keyMustContainLetterOrNumber),
              }),
        })}
        onSubmit={onSubmit}
      >
        <ClaimStageAddEditDialogInner isEdit={!!editedStage} onCancel={onClose} />
      </Formik>
    </CardDialog>
  );
};

export default ClaimStageAddEditDialog;
